import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Key Contacts directory — embedded under the Key Contacts category on the
// Resources page. A shared company phonebook: partnership leads, vendor
// support lines, escalation paths and internal experts, grouped by
// admin-configurable type chips, searchable, with one-tap copy for emails +
// phone numbers and a "Quick reach" pinned strip. Collapsed by default.
import { notify } from "../Toast";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../api/client";
import { safeHref } from "../../lib/safeUrl";
const LS_OPEN = "contacts-board-open";
// US aliases — when the country matches, the form shows a State field and the
// display includes the state.
const US_ALIASES = new Set([
    "us",
    "usa",
    "u.s.",
    "u.s.a.",
    "united states",
    "united states of america",
]);
function isUSA(country) {
    return US_ALIASES.has((country ?? "").trim().toLowerCase());
}
// "City, State, Country" for US contacts, "City, Country" elsewhere. Skips
// blank parts; returns "" when there's nothing to show.
function formatLocation(c) {
    const parts = [c.city, isUSA(c.country) ? c.state : null, c.country];
    return parts.filter((p) => p && p.trim()).join(", ");
}
function initials(name) {
    return (name
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase() ?? "")
        .join("") || "·");
}
function copy(text) {
    if (!text)
        return;
    try {
        void navigator.clipboard?.writeText(text);
    }
    catch {
        /* clipboard blocked — no-op */
    }
}
export default function KeyContactsBoard() {
    const [board, setBoard] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(() => {
        try {
            return localStorage.getItem(LS_OPEN) === "1";
        }
        catch {
            return false;
        }
    });
    const [search, setSearch] = useState("");
    const [activeType, setActiveType] = useState("all");
    const [editing, setEditing] = useState(null);
    const [managingTypes, setManagingTypes] = useState(false);
    // Pin to the Resources Quick-access strip. Writes localStorage + fires an
    // event the Resources page listens for (mirrors the CRM board's pin).
    const [pinned, setPinned] = useState(() => {
        try {
            return localStorage.getItem("kc-pinned") === "1";
        }
        catch {
            return false;
        }
    });
    function togglePinned() {
        setPinned((p) => {
            const next = !p;
            try {
                localStorage.setItem("kc-pinned", next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            window.dispatchEvent(new Event("kc:pin-changed"));
            return next;
        });
    }
    const load = useCallback(async () => {
        try {
            setBoard(await api.get("/contacts/board"));
        }
        catch {
            /* non-fatal */
        }
        finally {
            setLoaded(true);
        }
    }, []);
    useEffect(() => {
        void load();
    }, [load]);
    // Open-on-request: the Resources page (and the ⌘K palette's "Key Contacts"
    // tool) ask this board to expand via a localStorage intent (read once on
    // mount, in case the board isn't rendered yet) plus a live "kc:open" event
    // (when it's already mounted). Either path expands the board.
    useEffect(() => {
        function expand() {
            setOpen(true);
            try {
                localStorage.setItem(LS_OPEN, "1");
            }
            catch {
                /* ignore */
            }
        }
        try {
            if (localStorage.getItem("kc-open-intent") === "1") {
                localStorage.removeItem("kc-open-intent");
                expand();
            }
        }
        catch {
            /* ignore */
        }
        window.addEventListener("kc:open", expand);
        return () => window.removeEventListener("kc:open", expand);
    }, []);
    function toggleOpen() {
        setOpen((o) => {
            const next = !o;
            try {
                localStorage.setItem(LS_OPEN, next ? "1" : "0");
            }
            catch {
                /* ignore */
            }
            return next;
        });
    }
    const typesById = useMemo(() => {
        const m = new Map();
        for (const t of board?.types ?? [])
            m.set(t.id, t);
        return m;
    }, [board]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (board?.contacts ?? []).filter((c) => {
            if (activeType !== "all" && c.contact_type_id !== activeType)
                return false;
            if (!q)
                return true;
            return [c.name, c.title, c.organization, c.email, c.phone]
                .filter(Boolean)
                .some((v) => v.toLowerCase().includes(q));
        });
    }, [board, search, activeType]);
    const quickReach = useMemo(() => (board?.contacts ?? []).filter((c) => c.is_quick_reach), [board]);
    async function togglePin(c) {
        try {
            await api.patch(`/contacts/${c.id}`, { is_quick_reach: !c.is_quick_reach });
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update");
        }
    }
    async function remove(c) {
        if (!confirm(`Delete contact "${c.name}"?`))
            return;
        try {
            await api.del(`/contacts/${c.id}`);
            await load();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't delete");
        }
    }
    if (!loaded || !board)
        return null;
    return (_jsxs("div", { className: "kc-board", children: [_jsxs("button", { type: "button", className: "kc-board-header", onClick: toggleOpen, "aria-expanded": open, children: [_jsx("span", { className: "kc-board-title", children: "\uD83D\uDC64 Key Contacts directory" }), _jsxs("span", { className: "kc-board-summary muted small", children: [board.contacts.length, " ", board.contacts.length === 1 ? "contact" : "contacts"] }), _jsx("span", { className: "kc-board-caret", "aria-hidden": "true", children: open ? "▾" : "▸" })] }), open && (_jsxs("div", { className: "kc-board-body", children: [quickReach.length > 0 && (_jsxs("div", { className: "kc-quick", children: [_jsx("span", { className: "label", children: "\u2B50 Quick reach" }), _jsx("div", { className: "kc-quick-strip", children: quickReach.map((c) => (_jsxs("div", { className: "kc-quick-tile", children: [_jsx("span", { className: "kc-av", "aria-hidden": "true", children: initials(c.name) }), _jsxs("div", { className: "kc-quick-body", children: [_jsx("div", { className: "kc-quick-name", children: c.name }), _jsx("div", { className: "kc-quick-meta muted", children: [typesById.get(c.contact_type_id)?.name, c.organization]
                                                        .filter(Boolean)
                                                        .join(" · ") })] }), _jsxs("div", { className: "kc-quick-actions", children: [c.email && (_jsx("button", { type: "button", className: "kc-icon-btn", title: `Copy ${c.email}`, onClick: () => copy(c.email), children: "\u2709" })), c.phone && (_jsx("button", { type: "button", className: "kc-icon-btn", title: `Copy ${c.phone}`, onClick: () => copy(c.phone), children: "\u260E" }))] })] }, c.id))) })] })), _jsxs("div", { className: "kc-toolbar", children: [_jsx("input", { className: "kc-search", value: search, onChange: (e) => setSearch(e.target.value), placeholder: "\uD83D\uDD0D Search name, company, email, phone\u2026" }), _jsx("button", { type: "button", className: "btn-primary kc-add-btn", onClick: () => setEditing("new"), children: "+ Add contact" }), board.can_manage_types && (_jsx("button", { type: "button", className: "kc-stages-btn", onClick: () => setManagingTypes(true), children: "\u2699 Manage chips" })), _jsx("button", { type: "button", className: "kc-stages-btn", onClick: togglePinned, title: "Pin Key Contacts to Quick access", children: pinned ? "📌 Pinned" : "📌 Pin" })] }), _jsxs("div", { className: "kc-chips", children: [_jsx("button", { type: "button", className: `kc-chip ${activeType === "all" ? "active" : ""}`, onClick: () => setActiveType("all"), children: "All" }), board.types.map((t) => (_jsx("button", { type: "button", className: `kc-chip ${activeType === t.id ? "active" : ""}`, onClick: () => setActiveType(t.id), children: t.name }, t.id)))] }), _jsxs("div", { className: "kc-contacts-scroll", children: [board.types
                                .filter((t) => activeType === "all" || t.id === activeType)
                                .map((t) => {
                                const rows = filtered.filter((c) => c.contact_type_id === t.id);
                                if (rows.length === 0)
                                    return null;
                                return (_jsxs("div", { className: "kc-group", children: [_jsxs("div", { className: "kc-group-head", children: [_jsx("span", { className: "kc-group-dot", style: { background: t.color || "var(--blueberry)" }, "aria-hidden": "true" }), _jsx("span", { className: "kc-group-name", children: t.name }), _jsx("span", { className: "kc-group-count", children: rows.length })] }), rows.map((c) => (_jsx(ContactRow, { c: c, onEdit: () => setEditing(c), onDelete: () => remove(c), onTogglePin: () => togglePin(c) }, c.id)))] }, t.id));
                            }), filtered.length === 0 && (_jsxs("div", { className: "muted small kc-empty", children: ["No contacts match. ", search ? "Try a different search." : "Add one above."] }))] })] })), editing && (_jsx(ContactModal, { initial: editing === "new" ? null : editing, types: board.types, onClose: () => setEditing(null), onSaved: async () => {
                    setEditing(null);
                    await load();
                } })), managingTypes && (_jsx(TypesModal, { types: board.types, onClose: () => setManagingTypes(false), onChanged: load }))] }));
}
function ContactRow({ c, onEdit, onDelete, onTogglePin, }) {
    const location = formatLocation(c);
    const websiteHref = c.website ? safeHref(c.website) : null;
    return (_jsxs("div", { className: "kc-row", children: [_jsx("span", { className: "kc-av", "aria-hidden": "true", children: initials(c.name) }), _jsxs("div", { className: "kc-who", children: [_jsx("div", { className: "kc-nm", children: c.name }), (c.title || c.organization) && (_jsx("div", { className: "kc-ti muted small", children: [c.title, c.organization].filter(Boolean).join(" · ") })), (c.email || c.phone) && (_jsxs("div", { className: "kc-channels muted small", children: [c.email && (_jsx("a", { className: "kc-channel-link", href: `mailto:${c.email}`, children: c.email })), c.email && c.phone && _jsx("span", { "aria-hidden": "true", children: " \u00B7 " }), c.phone && (_jsx("a", { className: "kc-channel-link", href: `tel:${c.phone}`, children: c.phone }))] })), location && (_jsxs("div", { className: "kc-loc muted small", children: ["\uD83D\uDCCD ", location] })), websiteHref && websiteHref !== "#" && (_jsx("a", { className: "kc-website-link small", href: websiteHref, target: "_blank", rel: "noopener noreferrer", children: "\uD83C\uDF10 Website \u2197" }))] }), _jsxs("div", { className: "kc-row-actions", children: [c.email && (_jsx("button", { type: "button", className: "kc-copy-btn", title: `Copy ${c.email}`, onClick: () => copy(c.email), children: "\uD83D\uDCCB Copy email" })), c.phone && (_jsx("button", { type: "button", className: "kc-copy-btn", title: `Copy ${c.phone}`, onClick: () => copy(c.phone), children: "\uD83D\uDCCB Copy phone" })), c.can_edit && (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: `kc-icon-btn ${c.is_quick_reach ? "active" : ""}`, title: c.is_quick_reach ? "Unpin from Quick reach" : "Pin to Quick reach", onClick: onTogglePin, children: "\u2B50" }), _jsx("button", { type: "button", className: "btn-link", onClick: onEdit, children: "Edit" }), _jsx("button", { type: "button", className: "btn-link", onClick: onDelete, children: "Delete" })] }))] })] }));
}
function ContactModal({ initial, types, onClose, onSaved, }) {
    const [name, setName] = useState(initial?.name ?? "");
    const [title, setTitle] = useState(initial?.title ?? "");
    const [org, setOrg] = useState(initial?.organization ?? "");
    const [typeId, setTypeId] = useState(initial?.contact_type_id ?? types[0]?.id ?? "");
    const [email, setEmail] = useState(initial?.email ?? "");
    const [phone, setPhone] = useState(initial?.phone ?? "");
    const [website, setWebsite] = useState(initial?.website ?? "");
    // Location. Country defaults to USA so the State field shows by default;
    // State is only sent/displayed for US contacts.
    const [city, setCity] = useState(initial?.city ?? "");
    const [country, setCountry] = useState(initial?.country ?? "USA");
    const [stateVal, setStateVal] = useState(initial?.state ?? "");
    const [notes, setNotes] = useState(initial?.notes ?? "");
    const [quick, setQuick] = useState(initial?.is_quick_reach ?? false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const usa = isUSA(country);
    async function save() {
        if (!name.trim()) {
            setError("Give the contact a name.");
            return;
        }
        setSaving(true);
        setError(null);
        const payload = {
            name: name.trim(),
            title: title.trim() || null,
            organization: org.trim() || null,
            contact_type_id: typeId || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            website: website.trim() || null,
            city: city.trim() || null,
            // State only applies to US contacts; clear it otherwise.
            state: usa ? stateVal.trim() || null : null,
            country: country.trim() || null,
            notes: notes.trim() || null,
            is_quick_reach: quick,
        };
        try {
            if (initial)
                await api.patch(`/contacts/${initial.id}`, payload);
            else
                await api.post("/contacts", payload);
            onSaved();
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Couldn't save the contact");
            setSaving(false);
        }
    }
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal kc-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: initial ? "Edit contact" : "New contact" }), _jsxs("div", { className: "kc-form-grid", children: [_jsxs("label", { className: "kc-form-wide", children: ["Name", _jsx("input", { value: name, onChange: (e) => setName(e.target.value), autoFocus: true })] }), _jsxs("label", { children: ["Title / role", _jsx("input", { value: title, onChange: (e) => setTitle(e.target.value) })] }), _jsxs("label", { children: ["Organization", _jsx("input", { value: org, onChange: (e) => setOrg(e.target.value) })] }), _jsxs("label", { children: ["Type", _jsx("select", { value: typeId, onChange: (e) => setTypeId(e.target.value), children: types.map((t) => (_jsx("option", { value: t.id, children: t.name }, t.id))) })] }), _jsxs("label", { children: ["Email", _jsx("input", { value: email, onChange: (e) => setEmail(e.target.value) })] }), _jsxs("label", { children: ["Phone", _jsx("input", { value: phone, onChange: (e) => setPhone(e.target.value) })] }), _jsxs("label", { children: ["City", _jsx("input", { value: city, onChange: (e) => setCity(e.target.value) })] }), _jsxs("label", { children: ["Country", _jsx("input", { value: country, onChange: (e) => setCountry(e.target.value), placeholder: "USA" })] }), usa && (_jsxs("label", { children: ["State", _jsx("input", { value: stateVal, onChange: (e) => setStateVal(e.target.value), placeholder: "e.g. TX" })] })), _jsxs("label", { className: "kc-form-wide", children: ["Website", _jsx("input", { value: website, onChange: (e) => setWebsite(e.target.value) })] }), _jsxs("label", { className: "kc-form-wide", children: ["Notes", _jsx("textarea", { value: notes, onChange: (e) => setNotes(e.target.value), rows: 2 })] }), _jsxs("label", { className: "kc-form-wide kc-check", children: [_jsx("input", { type: "checkbox", checked: quick, onChange: (e) => setQuick(e.target.checked) }), "Pin to Quick reach"] })] }), error && _jsx("div", { className: "error small", children: error }), _jsxs("div", { className: "modal-actions", children: [_jsx("button", { onClick: onClose, disabled: saving, children: "Cancel" }), _jsx("button", { className: "btn-primary", onClick: save, disabled: saving, children: saving ? "Saving…" : initial ? "Save" : "Add contact" })] })] }) }));
}
function TypesModal({ types, onClose, onChanged, }) {
    const [busy, setBusy] = useState(false);
    const [newName, setNewName] = useState("");
    async function run(fn) {
        setBusy(true);
        try {
            await fn();
            await onChanged();
        }
        catch (e) {
            notify.error(e instanceof Error ? e.message : "Couldn't update chips");
        }
        finally {
            setBusy(false);
        }
    }
    const addType = () => {
        const name = newName.trim();
        if (!name)
            return;
        setNewName("");
        void run(() => api.post("/contacts/types", { name }));
    };
    const rename = (t) => {
        const name = prompt("Chip name", t.name);
        if (name == null)
            return;
        const v = name.trim();
        if (!v || v === t.name)
            return;
        void run(() => api.patch(`/contacts/types/${t.id}`, { name: v }));
    };
    const swap = (a, b) => run(async () => {
        await api.patch(`/contacts/types/${a.id}`, { order_index: b.order_index });
        await api.patch(`/contacts/types/${b.id}`, { order_index: a.order_index });
    });
    const remove = (t) => {
        if (!confirm(`Remove the "${t.name}" chip?`))
            return;
        void run(() => api.del(`/contacts/types/${t.id}`));
    };
    const ordered = [...types].sort((a, b) => a.order_index - b.order_index);
    return (_jsx("div", { className: "modal-backdrop", onClick: onClose, children: _jsxs("div", { className: "modal kc-modal", onClick: (e) => e.stopPropagation(), children: [_jsx("h3", { children: "Manage filter chips" }), _jsx("p", { className: "muted small", children: "These are the contact categories \u2014 they show as filter chips and group the directory. A chip with contacts in it can't be removed until those contacts are moved." }), _jsx("ul", { className: "kc-type-list", children: ordered.map((t, i) => (_jsxs("li", { className: "kc-type-row", children: [_jsx("span", { className: "kc-group-dot", style: { background: t.color || "var(--blueberry)" }, "aria-hidden": "true" }), _jsx("span", { className: "kc-type-name", children: t.name }), _jsxs("span", { className: "kc-type-tools", children: [_jsx("button", { type: "button", className: "btn-link", disabled: busy || i === 0, onClick: () => swap(t, ordered[i - 1]), title: "Move up", children: "\u2191" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy || i === ordered.length - 1, onClick: () => swap(t, ordered[i + 1]), title: "Move down", children: "\u2193" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => rename(t), children: "Rename" }), _jsx("button", { type: "button", className: "btn-link", disabled: busy, onClick: () => remove(t), children: "Remove" })] })] }, t.id))) }), _jsxs("div", { className: "kc-type-add", children: [_jsx("input", { value: newName, onChange: (e) => setNewName(e.target.value), placeholder: "New chip name" }), _jsx("button", { type: "button", className: "btn-primary", disabled: busy, onClick: addType, children: "+ Add chip" })] }), _jsx("div", { className: "modal-actions", children: _jsx("button", { onClick: onClose, children: "Done" }) })] }) }));
}
